<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import ContactNotes from 'dashboard/routes/dashboard/conversation/contact/ContactNotes.vue';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const router = useRouter();
const store = useStore();
const currentAccountId = useMapGetter('getCurrentAccountId');
const { width: windowWidth } = useWindowSize();
const miMarcianaFrame = ref(null);
const miMarcianaLoaded = ref(false);
const showMiMarcianaDirectos = ref(false);
const showCustomerNotes = ref(false);
const notifyDirectosVisibility = visible => {
  window.dispatchEvent(
    new CustomEvent('mi-marciana:directos-visibility', {
      detail: { visible },
    })
  );
};
const notesLabels = {
  section: 'Notas de la clienta',
  title: 'Notas',
  close: 'Cerrar notas',
  unlinked: 'Esta conversación no tiene una clienta vinculada.',
};
const directosTitle = 'Mi Marciana Directos';

const miMarcianaUrl = 'https://app.mimarcianaia.com/?embedded=chatwoot';
const miMarcianaDesktopUrl = `${miMarcianaUrl}&layout=desktop`;
const miMarcianaDirectosUrl =
  'https://directos.mimarcianaia.com/?embedded=chatwoot';

const currentMiMarcianaContext = computed(() => {
  const sender = props.currentChat.meta?.sender || {};
  const storedContact = sender.id
    ? store.getters['contacts/getContact']?.(sender.id) || {}
    : {};
  const contact = Object.keys(storedContact).length ? storedContact : sender;
  const assignee = props.currentChat.meta?.assignee || {};
  const additionalAttributes =
    contact.additional_attributes || sender.additional_attributes || {};
  const customAttributes =
    contact.custom_attributes || sender.custom_attributes || {};
  const rememberedInstagramIdentity = Array.isArray(
    additionalAttributes.mi_marciana_identities
  )
    ? additionalAttributes.mi_marciana_identities.find(
        identity =>
          String(identity?.type || '').toLowerCase() === 'instagram' &&
          identity?.value
      )?.value || ''
    : '';
  const instagramUsername =
    additionalAttributes.social_instagram_user_name ||
    rememberedInstagramIdentity ||
    '';
  const channel = props.currentChat.meta?.channel || '';
  const channelKey = String(channel).toLowerCase();
  const channelUsername =
    instagramUsername ||
    sender.identifier ||
    sender.phone_number ||
    sender.email ||
    '';

  return {
    accountId: String(currentAccountId.value || ''),
    conversationId: String(props.currentChat.id || ''),
    contactId: String(sender.id || props.currentChat.contact_id || ''),
    shopifyCustomerId: '',
    customerName: contact.name || sender.name || sender.available_name || '',
    socialName:
      contact.name || sender.name || sender.available_name || channelUsername,
    channelUsername,
    email: contact.email || sender.email || '',
    phone: contact.phone_number || sender.phone_number || '',
    instagramUsername,
    messengerId:
      additionalAttributes.social_facebook_user_id ||
      (channelKey.includes('facebook') || channelKey.includes('messenger')
        ? sender.identifier || ''
        : ''),
    avatarUrl:
      contact.thumbnail ||
      contact.avatar_url ||
      contact.avatar ||
      sender.thumbnail ||
      sender.avatar_url ||
      sender.avatar ||
      '',
    inboxId: String(props.currentChat.inbox_id || ''),
    channel,
    agent: assignee.name || '',
    recentProducts: Array.isArray(uiSettings.value?.mi_marciana_recent_products)
      ? uiSettings.value.mi_marciana_recent_products
      : [],
    pendingShippingAddress:
      customAttributes.mi_marciana_shipping_address || null,
    pendingShippingAddressStatus:
      customAttributes.mi_marciana_shipping_address_status || '',
  };
});

const sendMiMarcianaContext = () => {
  if (!miMarcianaLoaded.value || !miMarcianaFrame.value?.contentWindow) return;

  // Vue exposes computed values as reactive proxies. A proxy cannot cross the
  // window boundary through structured clone and caused DataCloneError, so the
  // embedded app never received the customer identifiers.
  const serializableContext = JSON.parse(
    JSON.stringify(currentMiMarcianaContext.value)
  );

  /*
   * Este componente solo transmite el contexto de la conversación.
   * No decide ni reinyecta la pantalla activa.
   */
  miMarcianaFrame.value.contentWindow.postMessage(
    {
      type: 'MI_MARCIANA_CONTEXT',
      context: serializableContext,
    },
    'https://app.mimarcianaia.com'
  );
};

const handleMiMarcianaLoad = async () => {
  miMarcianaLoaded.value = true;
  await nextTick();
  sendMiMarcianaContext();
};

const handleMiMarcianaNavigate = event => {
  const view = event.detail?.view;
  if (!view || !miMarcianaFrame.value?.contentWindow) return;

  /*
   * La navegación viaja separada del contexto. Así pulsar la barra no
   * vuelve a cargar clienta, bridge ni Workspace.
   */
  miMarcianaFrame.value.contentWindow.postMessage(
    {
      type: 'MI_MARCIANA_NAVIGATE',
      view,
    },
    'https://app.mimarcianaia.com'
  );
};

const openCustomerNotes = async () => {
  showCustomerNotes.value = !showCustomerNotes.value;
};

const closeCustomerNotes = () => {
  showCustomerNotes.value = false;
  window.dispatchEvent(new CustomEvent('mi-marciana:contact-notes-closed'));
};

const closeContactPanel = () => {
  if (uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const channelMatches = (value, requestedChannel) => {
  const channel = String(value || '').toLowerCase();
  const tokens = {
    whatsapp: ['whatsapp'],
    instagram: ['instagram'],
    messenger: ['facebook', 'messenger'],
    email: ['email', 'mail'],
  };
  return (tokens[requestedChannel] || []).some(token =>
    channel.includes(token)
  );
};

const conversationChannel = conversation => {
  const inboxId = conversation.inboxId || conversation.inbox_id;
  const inbox = inboxId
    ? store.getters['inboxes/getInboxById']?.(inboxId)
    : null;
  return [
    conversation.meta?.channel,
    conversation.channel,
    conversation.channelType,
    conversation.channel_type,
    inbox?.channel_type,
    inbox?.channelType,
  ]
    .filter(Boolean)
    .join(' ');
};

const sendChannelResult = (ok, channel, error = '') => {
  miMarcianaFrame.value?.contentWindow?.postMessage(
    {
      type: 'MI_MARCIANA_CHANNEL_RESULT',
      ok,
      channel,
      error,
    },
    'https://app.mimarcianaia.com'
  );
};

const sendMiMarcianaMessageResult = ({ requestId, ok, error = '' }) => {
  if (!requestId || !miMarcianaFrame.value?.contentWindow) return;

  miMarcianaFrame.value.contentWindow.postMessage(
    {
      type: 'MI_MARCIANA_SEND_RESULT',
      requestId,
      ok,
      error,
    },
    'https://app.mimarcianaia.com'
  );
};

const sendMiMarcianaMessage = async eventData => {
  const requestId = String(eventData?.requestId || '');
  const requestedConversationId = Number(eventData?.conversationId);
  const currentConversationId = Number(props.currentChat.id);
  const message = eventData?.message || {};
  const content = [message.text, message.url]
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .join('\n');

  if (!requestId) return;

  // El iframe nunca puede enviar a una conversación distinta de la visible.
  if (
    !requestedConversationId ||
    !currentConversationId ||
    requestedConversationId !== currentConversationId
  ) {
    sendMiMarcianaMessageResult({
      requestId,
      ok: false,
      error: 'La conversación ha cambiado. Vuelve a pulsar enviar.',
    });
    return;
  }

  if (!content) {
    sendMiMarcianaMessageResult({
      requestId,
      ok: false,
      error: 'El mensaje no contiene texto ni enlace.',
    });
    return;
  }

  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: currentConversationId,
      message: content,
      private: false,
    });
    sendMiMarcianaMessageResult({ requestId, ok: true });
  } catch (error) {
    sendMiMarcianaMessageResult({
      requestId,
      ok: false,
      error:
        error?.response?.data?.error ||
        error?.message ||
        'No se ha podido enviar el mensaje.',
    });
  }
};

const openChatwootChannel = async ({ channel }) => {
  if (!channel) return;

  const currentChannel = conversationChannel(props.currentChat);
  if (channelMatches(currentChannel, channel)) {
    closeContactPanel();
    sendChannelResult(true, channel);
    return;
  }

  const sender = props.currentChat.meta?.sender || {};
  const contactId = sender.id || props.currentChat.contact_id;
  if (!contactId) {
    sendChannelResult(
      false,
      channel,
      'Esta clienta no está vinculada a un contacto de Chatbot.'
    );
    return;
  }

  try {
    await store.dispatch('contactConversations/get', contactId);
    const conversations =
      store.getters['contactConversations/getAllConversationsByContactId'](
        contactId
      ) || [];
    const target = conversations.find(conversation =>
      channelMatches(conversationChannel(conversation), channel)
    );

    if (!target) {
      sendChannelResult(
        false,
        channel,
        'No hay ninguna conversación de esta clienta en esa bandeja.'
      );
      return;
    }

    closeContactPanel();
    await router.push({
      name: 'inbox_conversation',
      params: {
        accountId: currentAccountId.value,
        conversation_id: target.id,
      },
    });
    sendChannelResult(true, channel);
  } catch {
    sendChannelResult(
      false,
      channel,
      'No se ha podido abrir esa conversación en Chatbot.'
    );
  }
};

const handleMiMarcianaMessage = event => {
  if (event.origin === 'https://directos.mimarcianaia.com') {
    if (event.data?.type === 'MI_MARCIANA_DIRECTOS_CLOSE') {
      showMiMarcianaDirectos.value = false;
      notifyDirectosVisibility(false);
    }
    return;
  }

  if (event.origin !== 'https://app.mimarcianaia.com') return;

  if (event.data?.type === 'MI_MARCIANA_DIRECTOS_OPEN') {
    showCustomerNotes.value = false;
    showMiMarcianaDirectos.value = true;
    notifyDirectosVisibility(true);
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_UPDATE_CONTACT_ADDRESS') {
    const sender = props.currentChat.meta?.sender || {};
    const address = event.data.address;
    const status = String(event.data.status || 'pending_confirmation');
    if (!sender.id || !address?.address1) return;

    store.dispatch('contacts/update', {
      id: sender.id,
      customAttributes: {
        ...(sender.custom_attributes || {}),
        mi_marciana_shipping_address: address,
        mi_marciana_shipping_address_status: status,
      },
    });
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_READY') {
    miMarcianaLoaded.value = true;
    sendMiMarcianaContext();
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_SEND_MESSAGE') {
    sendMiMarcianaMessage(event.data);
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_CHAT') {
    closeContactPanel();
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_OPEN_CHANNEL') {
    openChatwootChannel(event.data);
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_OPEN_CONVERSATION') {
    const conversationId = Number(event.data.conversationId);
    if (!conversationId) return;
    closeContactPanel();
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: currentAccountId.value,
        conversation_id: conversationId,
      },
    });
    return;
  }

  if (event.data?.type === 'MI_MARCIANA_RECENT_PRODUCTS') {
    const products = Array.isArray(event.data.products)
      ? event.data.products.slice(0, 20)
      : [];
    updateUISettings({ mi_marciana_recent_products: products });
  }
};

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
  return !isSmallScreen.value || isContactSidebarOpen ? 0 : null;
});

watch(
  currentMiMarcianaContext,
  (nextContext, previousContext) => {
    if (JSON.stringify(nextContext) === JSON.stringify(previousContext)) return;
    sendMiMarcianaContext();
  },
  { deep: true }
);

watch(
  () => currentMiMarcianaContext.value.contactId,
  () => closeCustomerNotes()
);

onMounted(() => {
  window.addEventListener('mi-marciana:navigate', handleMiMarcianaNavigate);
  window.addEventListener('mi-marciana:open-contact-notes', openCustomerNotes);
  window.addEventListener('message', handleMiMarcianaMessage);
});

onBeforeUnmount(() => {
  notifyDirectosVisibility(false);
  window.removeEventListener('mi-marciana:navigate', handleMiMarcianaNavigate);
  window.removeEventListener(
    'mi-marciana:open-contact-notes',
    openCustomerNotes
  );
  window.removeEventListener('message', handleMiMarcianaMessage);
});
</script>

<template>
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
          '.mm-chat-bottom-nav',
          '.mm-desktop-splitter',
        ],
      },
    ]"
    class="fixed top-0 z-40 flex h-full flex-col overflow-hidden transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] md:border-n-weak md:bg-n-surface-2 md:shadow-none ltr:md:border-l rtl:md:border-r 2xl:w-[360px] 2xl:min-w-[360px]"
    :class="[
      isSmallScreen
        ? 'w-screen max-w-none bg-[#090909] shadow-2xl'
        : 'w-full max-w-sm bg-n-surface-2 shadow-lg',
      {
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      },
    ]"
  >
    <div class="relative flex flex-1 overflow-hidden">
      <iframe
        v-if="isSmallScreen"
        v-show="activeTab === 0"
        ref="miMarcianaFrame"
        :src="miMarcianaUrl"
        :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
        class="h-full w-full border-0 bg-[#f7f7f8]"
        allow="clipboard-read; clipboard-write"
        @load="handleMiMarcianaLoad"
      />

      <iframe
        v-else
        v-show="activeTab === 0"
        ref="miMarcianaFrame"
        :src="miMarcianaDesktopUrl"
        :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
        class="h-full w-full border-0 bg-[#f7f7f8]"
        allow="clipboard-read; clipboard-write"
        @load="handleMiMarcianaLoad"
      />

      <iframe
        v-if="showMiMarcianaDirectos"
        :src="miMarcianaDirectosUrl"
        :title="directosTitle"
        class="absolute inset-0 z-20 h-full w-full border-0 bg-[#f7f7f8]"
        allow="clipboard-read; clipboard-write"
      />
    </div>

    <section
      v-if="showCustomerNotes"
      class="absolute inset-0 z-50 flex flex-col bg-n-surface-1"
      :aria-label="notesLabels.section"
    >
      <header
        class="flex h-14 shrink-0 items-center justify-between border-b border-n-weak px-4"
      >
        <div class="min-w-0">
          <strong class="block truncate text-sm text-n-slate-12">
            {{ notesLabels.title }}
          </strong>
          <span class="block truncate text-xs text-n-slate-10">
            {{ currentMiMarcianaContext.customerName }}
          </span>
        </div>
        <button
          type="button"
          class="grid size-9 place-items-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :aria-label="notesLabels.close"
          @click="closeCustomerNotes"
        >
          <span class="i-ph-x-bold text-lg" />
        </button>
      </header>
      <div class="min-h-0 flex-1 overflow-y-auto">
        <ContactNotes
          v-if="currentMiMarcianaContext.contactId"
          :contact-id="currentMiMarcianaContext.contactId"
        />
        <p v-else class="p-6 text-center text-sm text-n-slate-11">
          {{ notesLabels.unlinked }}
        </p>
      </div>
    </section>
  </div>
</template>
